import {
  Component,
  inject,
  Inject,
  OnDestroy,
  signal,
  ViewChild,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AbbModalComponent } from '@components/abb-modal/abb-modal.component';
import { MODAL_DATA, ModalData } from '@components/abb-modal/modal';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { AbbButtonComponent } from '@components/abb-button/abb-button.component';
import {
  Roles,
  TransferUserAccountLicenseMutation,
  UserLicense,
  UserSelectOptionsQuery,
} from 'gql/graphql';
import { Subscription } from 'rxjs';
import { Apollo } from 'apollo-angular';
import { transferLicenseMutation } from '@graphql/account';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { CommonModule } from '@angular/common';
import { userSelectOptionsQuery } from '@graphql/user';
import { FilterOption } from '@components/abb-table/abb-table.component';
import { ROLES } from '@constants/user';
import { ToastService } from '@services/ui/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { CognitoService } from '@services/cognito/cognito.service';
import { getErrorMessage } from '@utils/errors';
import { t } from '@utils/strings';

export type TransferDataModalData = {
  account?: UserLicense;
};

@Component({
  selector: 'app-transfer-data-modal',
  standalone: true,
  template: `
    <abb-modal [title]="'License' | t" #modalRef>
      <form
        id="transfer-data-form"
        [formGroup]="form"
        (ngSubmit)="onSubmit()"
        class="flex flex-col gap-2"
      >
        <span class="text-xs font-medium uppercase"
          >{{ 'Transfer owner license to' | t }}:</span
        >
        <mat-form-field class="w-full" color="primary">
          <mat-select
            formControlName="newOwnerId"
            name="newOwnerId"
            placeholder="Select user"
            (selectionChange)="userSelected()"
            [panelClass]="'panel'"
          >
            <input
              class="px-4 min-h-12 outline-none w-full placeholder:text-abb-font/40 bg-transparent"
              (keydown)="$event.stopPropagation()"
              [formControl]="searchCtrl"
              [placeholder]="'Search' | t"
            />
            @for (option of userSelectOptions(); track option.value) {
            <mat-option
              [value]="option.value"
              [ngClass]="{
                '!hidden': !filteredUsers().includes(option.value)
              }"
              >{{
                option.skipTranslation ? option.label : (option.label | t)
              }}</mat-option
            >
            }
          </mat-select>
        </mat-form-field>
      </form>
      <div footer class="w-full flex flex-row gap-8 justify-end">
        <abb-button
          variant="clean"
          [disabled]="saving()"
          (onclick)="onCancel()"
          >{{ 'cancel' | t }}</abb-button
        >
        <abb-button
          class="w-32"
          type="submit"
          id="transfer-data-form-submit"
          form="transfer-data-form"
          [disabled]="form.invalid || saving()"
          [loading]="saving()"
          >{{ 'Save' | t }}</abb-button
        >
      </div>
    </abb-modal>
  `,
  styles: ``,
  imports: [
    CommonModule,
    AbbModalComponent,
    TPipe,
    AbbButtonComponent,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
  ],
  providers: [
    { provide: TRANSLATION_PREFIX, useValue: 'settings.tabs.account' },
  ],
})
export class TransferDataModalComponent
  implements ModalData<TransferDataModalData>, OnDestroy
{
  @ViewChild('modalRef') modalRef: AbbModalComponent | undefined;
  saving = signal(false);
  form = new FormGroup({
    newOwnerId: new FormControl<string>('', [Validators.required]),
  });
  searchCtrl = new FormControl<string>('');
  userSelectOptions = signal<FilterOption[]>([]);
  filteredUsers = signal<Array<string | number>>([]);
  prefix = inject(TRANSLATION_PREFIX);

  private subscriptions: Subscription[] = [];
  constructor(
    @Inject(MODAL_DATA) public data: TransferDataModalData,
    public apollo: Apollo,
    private toastService: ToastService,
    private translate: TranslateService,
    private readonly cognitoService: CognitoService
  ) {
    this.subscriptions.push(
      this.searchCtrl.valueChanges.subscribe((searchQuery: string | null) => {
        this.filteredUsers.set(
          searchQuery
            ? this._filter(searchQuery)
            : this.userSelectOptions()
                .map((p) => p.value)
                .slice()
        );
      }),
      apollo
        .watchQuery<UserSelectOptionsQuery>({
          query: userSelectOptionsQuery,
        })
        .valueChanges.subscribe({
          next: ({ data }) => {
            let userSelectOptions = data.userList.map((o) => {
              return {
                value: o.id,
                role: o.role,
                label: `${o.name} ${o.lastName}`,
                skipTranslation: true,
              };
            });

            userSelectOptions = userSelectOptions.filter(
              (user) =>
                user.value !== cognitoService.loggedUser()?.id &&
                user.role !== Roles.Admin
            );

            this.userSelectOptions.set(userSelectOptions);
            this.filteredUsers.set(
              userSelectOptions.map((o) => o.value).slice()
            );
          },
          error: (err) => {
            //let errMsg = getErrorMessage(err, this.translate);
            //this.toastService.notifyError(errMsg);
          },
        })
    );
  }
  userSelected(): void {
    this.searchCtrl.setValue(null);
  }

  private _filter(searchQuery: string): Array<string | number> {
    return this.userSelectOptions()
      .filter((u) =>
        u.label.toLowerCase().trim().includes(searchQuery.toLowerCase())
      )
      .map((u) => u.value);
  }

  onCancel() {
    this.modalRef?.dispose();
  }

  onSubmit(): void {
    if (this.form.invalid) {
      return;
    }
    this.saving.set(true);
    this.subscriptions.push(
      this.apollo
        .mutate<TransferUserAccountLicenseMutation>({
          mutation: transferLicenseMutation,
          variables: {
            fromUserId: this.cognitoService.loggedUser()?.id,
            toUserId: this.form.value.newOwnerId,
            licenseId: this.data.account?.id,
          },
        })
        .subscribe({
          next: (result) => {
            this.saving.set(false);
            this.toastService.notifySuccess(
              t(
                this.prefix,
                `License {id} transferred successfully to user {name} {surname}`,
                this.translate,
                {
                  id: result.data?.transferUserAccountLicense.license?.id,
                  name: result.data?.transferUserAccountLicense.name,
                  surname: result.data?.transferUserAccountLicense.lastName,
                }
              )
            );
          },
          error: () => {
            this.saving.set(false);
            this.toastService.notifyError(
              t(
                this.prefix,
                `An error occurred while transferring license to user {fullName}`,
                this.translate,
                {
                  fullName: this.userSelectOptions().find(
                    (u) => u.value == this.form.value.newOwnerId
                  )?.label,
                }
              )
            );
          },
          complete: () => {
            this.modalRef?.dispose();
          },
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
